import type {OrderStatus} from "../../api/models";

// The signed-in user's own pages: profile, two-factor authentication, licenses and orders.
export const account = {
    profile: {
        title: 'Profile',
        details: {
            title: 'Details',
            saved: 'Saved.',
            username: 'Username',
            displayName: 'Display name',
            save: 'Save'
        },
        avatar: {
            label: 'Picture',
            help: 'PNG, JPEG or GIF, up to 10 MB. Larger than {max} × {max} pixels is scaled down. An animated picture shows its first frame, and photo details such as location are removed.',
            // The label screen readers give the upload's progress bar.
            progress: 'Upload progress',
            remove: 'Remove picture',
            // Found before uploading (src/components/avatarImage.ts).
            wrongType: 'Use a PNG, JPEG or GIF image.',
            tooLarge: 'Use an image of 10 MB or less.',
            unreadable: "That image couldn't be read. Try another file.",
            notResized: "That image couldn't be resized. Try another file."
        },
        email: {
            title: 'Email address',
            current: 'Current address: <strong>{email}</strong>',
            confirmed: 'Confirmed',
            notConfirmed: 'Not confirmed',
            confirmedHelp: 'You can sign in with this address or your username.',
            notConfirmedHelp: 'Confirm this address to sign in with it as well as your username.',
            // Mail to it bounces (SES reported it).
            notDelivering: 'Not delivering',
            notDeliveringHelp: "Emails to this address are being returned as undeliverable, so you won't get password resets or sign-in codes. Change it below, or if the mailbox works again, confirm it to let us know.",
            confirmThis: 'Confirm this address',
            sent: "We've sent a confirmation link to <strong>{email}</strong>. Open it within 10 minutes to confirm it.",
            // Added after sent when the link is for a new address.
            sentUntilThen: ' Until then, your address stays the same.',
            newEmail: 'New email address',
            submit: 'Send confirmation link'
        },
        password: {
            title: 'Password',
            changed: 'Your password has been changed.',
            current: 'Current password',
            currentRequired: 'Enter your current password.',
            newPassword: 'New password',
            confirmPassword: 'Confirm new password',
            submit: 'Change password'
        }
    },

    twoFactor: {
        title: 'Two-factor authentication',
        intro: 'Ask for a code as well as your password when you sign in. Turn on either method or both.',
        totp: {
            title: 'Authenticator app',
            description: 'Codes from an app such as Google Authenticator, Microsoft Authenticator or 1Password.'
        },
        email: {
            title: 'Email codes',
            description: 'Codes sent to <strong>{email}</strong>.'
        },
        on: 'On',
        off: 'Off',
        turnOn: 'Turn on',
        turnOff: 'Turn off',
        recoveryCodes: 'Recovery codes',
        recoveryCodesRemaining: "{count} unused. Each one signs you in once if you can't get a code.",
        regenerate: 'Regenerate',
        generate: 'Generate',
        notices: {
            totpRemoved: 'The authenticator app has been removed.',
            totpSetUp: 'Your authenticator app is set up.',
            emailOff: 'Email codes have been turned off.',
            emailOn: 'Email codes have been turned on.'
        },
        // Titles of the dialog asking for the password before a change.
        actions: {
            'enable-totp': 'Set up an authenticator app',
            'disable-totp': 'Turn off the authenticator app',
            'enable-email': 'Turn on email codes',
            'disable-email': 'Turn off email codes',
            'regenerate': 'Generate new recovery codes'
        },
        passwordIncorrect: 'The password is incorrect.',
        alreadyDone: 'That has already been done. Reload the page to see the current settings.',
        tooManyAttempts: 'Too many attempts. Wait a few minutes and try again.',
        codeIncorrect: "That code didn't match, or it has expired.",
        confirmPassword: 'Confirm your password',
        continue: 'Continue',
        code: 'Code',
        totpSetup: {
            title: 'Set up an authenticator app',
            scan: 'Scan this QR code with your authenticator app.',
            enterCode: 'Enter the 6-digit code it shows.',
            qrAlt: 'QR code for your authenticator app',
            cantScan: "Can't scan it? Enter this key instead:",
            copyKey: 'Copy key'
        },
        emailSetup: {
            title: 'Turn on email codes',
            sent: "We've sent a 6-digit code to <strong>{email}</strong>. Enter it within 10 minutes.",
            help: 'No email? Cancel and turn email codes on again to get a new code.'
        },
        // Shown once, after recovery codes are made.
        savedCodes: {
            title: 'Save your recovery codes',
            text: "If you lose access to your authenticator app, each of these codes signs you in once. Keep them somewhere safe. <strong>They won't be shown again.</strong>",
            copy: 'Copy codes',
            download: 'Download',
            // The first line of the downloaded file, and the file's name.
            fileHeading: 'Tristar Advanced recovery codes',
            fileName: 'tristar-advanced-recovery-codes.txt',
            done: "I've saved them"
        }
    },

    licenses: {
        title: 'My licenses',
        alreadyDeactivated: 'That computer was already deactivated.',
        none: 'There are no licenses on your account.',
        unknownProduct: 'Unknown product',
        revoked: 'Revoked',
        // Instead of the edition, on an Early Access license (the Early Access Program's).
        earlyAccess: 'Early Access',
        earlyAccessHint: 'Activates the Early Access Versions only. Your other licenses activate the released versions.',
        // Instead of the edition, on a reviewer's license (from a press inquiry), with when it ends.
        review: 'Review license',
        reviewUntil: 'Until {date}',
        reviewKept: 'No end date',
        copyKey: 'Copy license key',
        downloads: 'Downloads',
        summary: 'Licensed to {licensee}. Active on <strong>{active} of {seats}</strong> computers; {used} of {perYear} new activations used in the last 12 months.',
        noActivations: 'Not active on any computer.',
        columns: {
            computer: 'Computer',
            system: 'System',
            version: 'Version',
            lastSeen: 'Last seen'
        },
        unnamedComputer: 'Unnamed computer',
        // Beside the version of a computer running a CI or development build (-dev), which takes no seat.
        devBuild: 'CI build',
        devBuildHint: "Development builds don't use a seat or one of the yearly activations.",
        deactivate: 'Deactivate',
        confirm: {
            title: 'Deactivate this computer?',
            text: '<strong>{computer}</strong> will stop running {product} the next time it renews its license online, or when its current lease runs out.',
            // Stand-ins for a computer or product without a name.
            thisComputer: 'This computer',
            theProduct: 'the product',
            // <em> is the button's name in the product.
            note: "Freeing the seat doesn't give back one of the {perYear} new activations allowed each year. If you still have the computer, use <em>Deactivate this computer</em> in the product's license dialog instead: it stops straight away."
        }
    },

    orders: {
        title: 'My orders',
        none: "You haven't bought anything yet. <link>See the products</link>",
        columns: {
            date: 'Date',
            order: 'Order',
            product: 'Product',
            licensedTo: 'Licensed to',
            total: 'Total',
            paidWith: 'Paid with',
            status: 'Status'
        },
        // An order's product and edition.
        product: '{product}, {edition}',
        statuses: {
            pending: 'Processing',
            fulfilled: 'Paid',
            refunded: 'Refunded',
            cancelled: 'Expired',
            failed: 'Declined'
        } satisfies Record<OrderStatus, string>,
        // How an order was paid: PayPal, or a card through Stripe.
        providers: {
            paypal: 'PayPal',
            stripe: 'Card',
            paddle: 'Paddle'
        },
        footer: 'Your license keys are on <link>My licenses</link>. For a refund or a question about an order, <ticket>open a support ticket</ticket> with its order number.'
    }
};

export type AccountMessages = typeof account;
